import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
} from '@mui/material'
import { PersonAddAlt1, Search, HandshakeOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import logo from '../assets/logo.png'
import runnerImage from '../assets/runner.jpg'
import jumperImage from '../assets/jumper.jpg'
import trackImage from '../assets/track.jpg'
import { PublicFooter } from '../components/PublicFooter'
import { PublicNavbar } from '../components/PublicNavbar'
import { RECRUITER_PRICE_RANGE_USD } from '@tnf-recruit/shared'
import {
  LIMITED_TIME_PRICING_LABEL,
  RECRUITER_TIER_ROWS,
  formatDiscountedUsd,
  formatUsd,
} from '../lib/pricing'
import { OfferPrice } from '../components/OfferPrice'
import { LaunchOfferChip } from '../components/LaunchOfferChip'
import { useLaunchOffer } from '../hooks/useLaunchOffer'

const NAVY_OVERLAY = 'linear-gradient(rgba(8,27,64,0.72), rgba(8,27,64,0.72))'

export const LandingPage = () => {
  const navigate = useNavigate()
  const launchOffer = useLaunchOffer()
  const discountPercent = launchOffer?.active ? launchOffer.percentOff : null

  const handleGetStarted = () => navigate('/signup')

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <PublicNavbar />

      <Box
        sx={{
          position: 'relative',
          color: 'common.white',
          backgroundImage: `${NAVY_OVERLAY}, url(${runnerImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          py: { xs: 10, md: 14 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h4"
                sx={{ letterSpacing: 3, color: 'warning.light', fontWeight: 700 }}
              >
                TRACK & FIELD RECRUITING
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mt: 1,
                  mb: 2,
                  fontSize: { xs: '2.25rem', md: '3.5rem' },
                }}
              >
                Get Discovered by College Coaches
              </Typography>
              <Typography
                variant="h6"
                sx={{ maxWidth: 640, color: 'grey.200', fontWeight: 400, mb: 4 }}
              >
                Build a standout athletic profile and get seen by verified college recruiters across
                the country.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{ px: 4 }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    borderColor: 'common.white',
                    color: 'common.white',
                    px: 4,
                    '&:hover': { borderColor: 'warning.light', color: 'warning.light' },
                  }}
                >
                  I'm a Recruiter
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ mt: 3, color: 'grey.300' }}>
                Free for the first 1,000 athletes — then a one-time $15 to create an account.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src={logo}
                alt=""
                sx={{
                  width: '100%',
                  maxWidth: 280,
                  mx: 'auto',
                  display: 'block',
                  filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.45))',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          sx={{ fontWeight: 700 }}
          gutterBottom
        >
          How It Works
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Three simple steps from profile to recruitment.
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <PersonAddAlt1 sx={{ fontSize: 40 }} />,
              title: 'Create your profile',
              body: 'Add your personal records, academic info, highlights, and events.',
            },
            {
              icon: <Search sx={{ fontSize: 40 }} />,
              title: 'Get discovered',
              body: 'Verified college coaches search and save athlete profiles that match their programs.',
            },
            {
              icon: <HandshakeOutlined sx={{ fontSize: 40 }} />,
              title: 'Connect',
              body: 'Recruiters reach out directly when they see a fit — you control what you share.',
            },
          ].map(step => (
            <Grid item xs={12} md={4} key={step.title}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>{step.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.body}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {[
              {
                image: jumperImage,
                title: 'For Athletes',
                bullets: [
                  'Build a profile recruiters trust',
                  'Showcase PRs, academics, and highlights',
                  'Free for the first 1,000 athletes',
                ],
                cta: 'Start your profile',
              },
              {
                image: trackImage,
                title: 'For Recruiters',
                bullets: [
                  'Search thousands of verified athletes',
                  'Filter by events, location, and academics',
                  'Direct contact with top prospects',
                ],
                cta: 'Browse athletes',
              },
            ].map(tile => (
              <Grid item xs={12} md={6} key={tile.title}>
                <Box
                  sx={{
                    position: 'relative',
                    minHeight: 340,
                    borderRadius: 3,
                    overflow: 'hidden',
                    color: 'common.white',
                    backgroundImage: `${NAVY_OVERLAY}, url(${tile.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    p: 4,
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
                    {tile.title}
                  </Typography>
                  <Stack spacing={1} sx={{ mb: 3 }}>
                    {tile.bullets.map(b => (
                      <Typography key={b} variant="body1" sx={{ color: 'grey.200' }}>
                        • {b}
                      </Typography>
                    ))}
                  </Stack>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleGetStarted}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {tile.cta}
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          sx={{ fontWeight: 700 }}
          gutterBottom
        >
          Simple, Transparent Pricing
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
          Free early access for athletes. Affordable for recruiters.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%', p: 2, border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Typography
                  variant="overline"
                  color="primary"
                  sx={{ fontWeight: 700, letterSpacing: 2 }}
                >
                  ATHLETES
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                  Free
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  for the first 1,000 members — then a one-time $15 fee
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                  <Chip label="Unlimited profile views" color="primary" />
                  <Chip label="Academic & athletic data" color="primary" />
                  <Chip label="Video highlights" color="primary" />
                </Stack>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  fullWidth
                  onClick={handleGetStarted}
                >
                  Create Your Profile
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%', p: 2 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography
                    variant="overline"
                    color="primary"
                    sx={{ fontWeight: 700, letterSpacing: 2 }}
                  >
                    RECRUITERS
                  </Typography>
                  <Chip size="small" color="warning" label={LIMITED_TIME_PRICING_LABEL} />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                  From{' '}
                  <OfferPrice
                    original={formatUsd(RECRUITER_PRICE_RANGE_USD.min)}
                    discounted={
                      discountPercent
                        ? formatDiscountedUsd(RECRUITER_PRICE_RANGE_USD.min, discountPercent)
                        : undefined
                    }
                  />
                  <Typography component="span" variant="body1" color="text.secondary">
                    {' '}
                    / year
                  </Typography>
                </Typography>
                {launchOffer?.active && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <LaunchOfferChip offer={launchOffer} size="small" />
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Full access to the verified student-athlete database, priced by your school&apos;s
                  division
                </Typography>
                <Stack sx={{ mb: 3 }}>
                  {RECRUITER_TIER_ROWS.map(tier => (
                    <Stack
                      key={tier.label}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {tier.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        <OfferPrice
                          original={formatUsd(tier.usd)}
                          discounted={
                            discountPercent
                              ? formatDiscountedUsd(tier.usd, discountPercent)
                              : undefined
                          }
                        />
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}
                          / yr
                        </Typography>
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                  <Chip label="Unlimited athlete profiles" />
                  <Chip label="Advanced search & filters" />
                  <Chip label="Contact information" />
                </Stack>
                <Button variant="outlined" size="large" fullWidth onClick={handleGetStarted}>
                  Sign Up as Recruiter
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <PublicFooter />
    </Box>
  )
}
